// Authentication Service - Unified with partner-admin and web-app-partner
import { API_ENDPOINTS } from "./config.js";

// Safari detection utility - Safe for SSR/Build
const isSafari = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const userAgent = navigator.userAgent;
  return (
    /^((?!chrome|android).)*safari/i.test(userAgent) ||
    /iPad|iPhone|iPod/.test(userAgent)
  );
};

// Cross-browser persistent storage - Safe for SSR/Build
const persistentStorage = {
  setItem: (key, value) => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(key, value);
      if (isSafari()) {
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn("Storage failed:", error);
      try {
        sessionStorage.setItem(key, value);
      } catch (_e) {
        // Silent fail
      }
    }
  },

  getItem: (key) => {
    if (typeof window === "undefined") return null;

    try {
      let value = localStorage.getItem(key);
      if (!value && isSafari()) {
        value = sessionStorage.getItem(key);
        if (value) {
          localStorage.setItem(key, value);
        }
      }
      return value;
    } catch (error) {
      console.warn("Storage read failed:", error);
      try {
        return sessionStorage.getItem(key);
      } catch (_e) {
        return null;
      }
    }
  },

  removeItem: (key) => {
    if (typeof window === "undefined") return;

    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn("Storage removal failed:", error);
    }
  },
};

const handleAuthError = (error, context) => {
  console.error(`Authentication error in ${context}:`, error);

  // Only clear tokens for actual auth failures, not network errors
  const errorMessage = error instanceof Error ? error.message : String(error);
  const shouldClearTokens =
    errorMessage.includes("Refresh failed") ||
    errorMessage.includes("No refresh token") ||
    errorMessage.includes("Authentication failed") ||
    errorMessage.includes("Invalid refresh token") ||
    errorMessage.includes("401");

  if (shouldClearTokens) {
    AuthService.clearTokens();
  }
};

export class AuthService {
  static _refreshPromise = null;
  static _tokenExpiryTime = null;
  static _sessionRegisterPromise = null;

  // Token management
  static getAccessToken() {
    return persistentStorage.getItem("tringAuthToken");
  }

  static setAccessToken(token, expiresIn = 3600) {
    if (token) {
      const validExpiresIn = Number(expiresIn) > 0 ? Number(expiresIn) : 3600;
      persistentStorage.setItem("tringAuthToken", token);
      this._tokenExpiryTime = Date.now() + validExpiresIn * 1000;

      // Standard key from web-app-partner
      persistentStorage.setItem(
        "tringTokenExpiry",
        this._tokenExpiryTime.toString(),
      );
      // Legacy key for backwards compatibility
      persistentStorage.setItem(
        "tringAuthToken_expiry",
        this._tokenExpiryTime.toString(),
      );

      if (isSafari()) {
        const safariExpiry = Date.now() + validExpiresIn * 1000 * 2;
        persistentStorage.setItem(
          "tringTokenExpiry_safari",
          safariExpiry.toString(),
        );
        persistentStorage.setItem(
          "tringAuthToken_expiry_safari",
          safariExpiry.toString(),
        );
      }
    } else {
      persistentStorage.removeItem("tringAuthToken");
      persistentStorage.removeItem("tringTokenExpiry");
      persistentStorage.removeItem("tringTokenExpiry_safari");
      persistentStorage.removeItem("tringAuthToken_expiry");
      persistentStorage.removeItem("tringAuthToken_expiry_safari");
      this._tokenExpiryTime = null;
    }
  }

  static getRefreshToken() {
    // Check standard key first, then fallback key
    return (
      persistentStorage.getItem("tringRefreshToken") ||
      persistentStorage.getItem("tringAuthToken_refresh")
    );
  }

  static setRefreshToken(token) {
    if (token) {
      // Set both standard key and legacy key for full compatibility
      persistentStorage.setItem("tringRefreshToken", token);
      persistentStorage.setItem("tringAuthToken_refresh", token);
    } else {
      persistentStorage.removeItem("tringRefreshToken");
      persistentStorage.removeItem("tringAuthToken_refresh");
      persistentStorage.removeItem("tringAuthToken_refresh_expiry");
    }
  }

  static isTokenExpired() {
    // If no access token exists at all, it's expired
    const token = this.getAccessToken();
    if (!token) return true;

    const expiryTime =
      persistentStorage.getItem("tringTokenExpiry") ||
      persistentStorage.getItem("tringAuthToken_expiry");

    // If access token exists in storage but expiry timestamp is missing, do not expire prematurely
    if (!expiryTime) return false;

    const expiry = parseInt(expiryTime, 10);
    if (isNaN(expiry)) return false;
    this._tokenExpiryTime = expiry;

    if (isSafari()) {
      const safariExpiry =
        persistentStorage.getItem("tringTokenExpiry_safari") ||
        persistentStorage.getItem("tringAuthToken_expiry_safari");

      if (safariExpiry) {
        const sExp = parseInt(safariExpiry, 10);
        if (!isNaN(sExp)) {
          // 60-second buffer before expiry to avoid in-flight expiration
          return Date.now() >= sExp - 60000;
        }
      }
    }

    // 60-second buffer before expiry to avoid in-flight expiration
    return Date.now() >= expiry - 60000;
  }

  static isRefreshTokenExpired() {
    // If a refresh token exists, allow the server endpoint to validate it
    const token = this.getRefreshToken();
    return !token;
  }

  static clearTokens() {
    persistentStorage.removeItem("tringAuthToken");
    persistentStorage.removeItem("tringTokenExpiry");
    persistentStorage.removeItem("tringTokenExpiry_safari");
    persistentStorage.removeItem("tringRefreshToken");
    persistentStorage.removeItem("tringAuthToken_refresh");
    persistentStorage.removeItem("tringAuthToken_expiry");
    persistentStorage.removeItem("tringAuthToken_expiry_safari");
    persistentStorage.removeItem("tringAuthToken_refresh_expiry");
    persistentStorage.removeItem("tringAuthToken_safari_flag");

    persistentStorage.removeItem("userId");
    persistentStorage.removeItem("artistId");
    persistentStorage.removeItem("businessId");
    persistentStorage.removeItem("PerformerId");
    persistentStorage.removeItem("sessionId");
    persistentStorage.removeItem("isUserRegistered");

    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("tringAuthToken");
        sessionStorage.removeItem("tringAuthToken");
        localStorage.removeItem("tringRefreshToken");
        sessionStorage.removeItem("tringRefreshToken");
        localStorage.removeItem("isUserRegistered");
      } catch (_e) {
        // Silent fail
      }
    }

    this._tokenExpiryTime = null;
    this._refreshPromise = null;
    this._sessionRegisterPromise = null;
  }

  static isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired();
  }

  static async refreshUserToken() {
    if (this._refreshPromise) {
      return await this._refreshPromise;
    }

    this._refreshPromise = (async () => {
      try {
        const currentRefreshToken = this.getRefreshToken();
        if (!currentRefreshToken) {
          this.clearTokens();
          if (
            typeof window !== "undefined" &&
            window.location.pathname !== "/" &&
            window.location.pathname !== "/login"
          ) {
            window.location.href = "/";
          }
          throw new Error("No refresh token available - redirecting to login");
        }

        const refreshUrl =
          API_ENDPOINTS.USER_AUTH_REFRESH ||
          API_ENDPOINTS.AUTH_REFRESH ||
          "https://test-api.tringbox.com/v1/auth/refresh";

        const response = await fetch(refreshUrl, {
          method: "POST",
          headers: {
            "X-Platform": "browser",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Only clear tokens if the refresh token is truly rejected (401/403/400)
          if (
            response.status === 401 ||
            response.status === 403 ||
            response.status === 400
          ) {
            this.clearTokens();
          }
          throw new Error(
            `Refresh failed: ${errorData.message || response.statusText}`,
          );
        }

        const data = await response.json();
        const accessToken = data.accessToken || data.tokens?.accessToken;
        const expiresIn = data.expiresIn || data.tokens?.expiresIn || 3600;
        const newRefreshToken =
          data.refreshToken || data.tokens?.refreshToken || currentRefreshToken;

        if (!accessToken) {
          throw new Error("Refresh failed: No access token in response");
        }

        this.setAccessToken(accessToken, expiresIn);
        if (newRefreshToken) {
          this.setRefreshToken(newRefreshToken);
        }

        return accessToken;
      } catch (error) {
        handleAuthError(error, "refreshUserToken");
        throw error;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return await this._refreshPromise;
  }

  // Alias for backward compatibility across web-app-request
  static async refreshToken() {
    return await this.refreshUserToken();
  }

  // Handle verify OTP response
  static async handleVerifyOtpResponse(verifyResponse) {
    if (this._sessionRegisterPromise) {
      return await this._sessionRegisterPromise;
    }

    this._sessionRegisterPromise = (async () => {
      try {
        const { tokens, id, isUserRegistered, role, isBusinessUser } =
          verifyResponse;

        if (!tokens?.accessToken) {
          throw new Error("No access token received from verify OTP response");
        }

        this.setAccessToken(tokens.accessToken, tokens.expiresIn || 3600);
        if (tokens?.refreshToken) {
          this.setRefreshToken(tokens.refreshToken);
        }

        persistentStorage.setItem("userId", id || "");
        persistentStorage.setItem("PerformerId", id || "");

        const isBusiness = isBusinessUser === true;
        persistentStorage.setItem("isBusiness", isBusiness.toString());
        persistentStorage.setItem(
          "isUserRegistered",
          (isUserRegistered === true).toString()
        );

        if (role) {
          persistentStorage.setItem("userRole", JSON.stringify(role));
          persistentStorage.setItem("isWaiter", role.includes("Waiter"));
          persistentStorage.setItem("isKj", role.includes("KJ"));
        }

        return {
          userId: id,
          accessToken: tokens.accessToken,
          isUserRegistered: isUserRegistered,
          isBusinessUser: isBusiness,
          role: role,
          userData: verifyResponse,
        };
      } catch (error) {
        console.error("Error handling verify OTP response:", error);
        throw error;
      }
    })().finally(() => {
      this._sessionRegisterPromise = null;
    });

    return await this._sessionRegisterPromise;
  }

  static isUserRegistered() {
    return persistentStorage.getItem("isUserRegistered") === "true";
  }

  static setUserRegistered(isRegistered) {
    persistentStorage.setItem(
      "isUserRegistered",
      (isRegistered === true).toString()
    );
  }

  static isBusinessUser() {
    return persistentStorage.getItem("isBusiness") === "true";
  }

  static setBusinessUser(isBusiness) {
    persistentStorage.setItem("isBusiness", (!!isBusiness).toString());
  }

  static getAuthToken() {
    return this.getAccessToken();
  }

  static setAuthToken(token, type = "auth") {
    this.setAccessToken(token, 3600);
  }

  static clearAuthTokens() {
    this.clearTokens();
  }
}

// Authenticated fetch utility matching web-app-partner with race-condition prevention
export const fetchWithAuth = async (url, options = {}) => {
  try {
    if (AuthService.isTokenExpired()) {
      try {
        await AuthService.refreshUserToken();
      } catch (e) {
        console.warn(
          "Proactive token refresh failed, attempting fetch with current token:",
          e,
        );
      }
    }

    let authToken = AuthService.getAccessToken();
    if (!authToken) {
      try {
        authToken = await AuthService.refreshUserToken();
      } catch (e) {
        handleAuthError(
          new Error("No access token available"),
          "fetchWithAuth - initial check",
        );
      }
    }

    const initialHeaders = {
      "Content-Type": "application/json",
      ...options.headers,
    };
    if (authToken) {
      initialHeaders.Authorization = `Bearer ${authToken}`;
    }

    let response = await fetch(url, {
      ...options,
      headers: initialHeaders,
    });

    // If 401 Unauthorized, try refreshing token once
    if (response.status === 401) {
      try {
        const latestStoredToken = AuthService.getAccessToken();
        let newToken = latestStoredToken;

        // If another concurrent request has already refreshed the token,
        // use the latest token directly without hitting refresh endpoint again!
        if (!latestStoredToken || latestStoredToken === authToken) {
          newToken = await AuthService.refreshUserToken();
        }

        const retryHeaders = {
          "Content-Type": "application/json",
          "x-platform": "browser",
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        };

        response = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      } catch (refreshError) {
        // If refresh fails and we're in browser, redirect to login
        if (typeof window !== "undefined") {
          AuthService.clearTokens();
          if (
            window.location.pathname !== "/" &&
            window.location.pathname !== "/login"
          ) {
            window.location.href = "/";
          }
        }
        handleAuthError(refreshError, "fetchWithAuth - token refresh");
        throw new Error("Authentication failed. Please login again.");
      }
    }

    return response;
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message.includes("Authentication failed") ||
        error.message.includes("No access token"))
    ) {
      handleAuthError(error, "fetchWithAuth - auth error");
    } else {
      console.warn("Network error in fetchWithAuth:", error);
    }
    throw error;
  }
};

// Legacy exports for backward compatibility
export const getAuthToken = () => AuthService.getAuthToken();
export const isAuthenticated = () => AuthService.isAuthenticated();
export const clearAuthTokens = () => AuthService.clearAuthTokens();
export const setAuthToken = (token, type) =>
  AuthService.setAuthToken(token, type);

export default AuthService;

